/**
 * AdminSocialStaff Page Component
 * Administrative interface for adding, editing, and removing social club staff profiles,
 * persisting custom staff records to LocalStorage, and logging audit entries.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { appendAuditLogEntry } from '../../utils/auditLog';
import { resolveAdminRole } from '../../utils/adminRole';
import '../../assets/fontello-plus/fontello-73784b09/css/calander.css';
import '../../assets/fontello-change/fontello-79be5077/css/fontello.css';
import logo from '../../assets/finalclipstonedesginlogo-removebg-preview.png';

const SOCIAL_STAFF_STORE_KEY = 'clipstone_social_staff';

const defaultStaff = [
  {
    id: 'kelly-jackson',
    name: 'Kelly Jackson',
    role: 'Office Manager',
    phone: '01623 622098',
    email: 'clipstonesocialclub@yahoo.com',
  },
  {
    id: 'chanel-belshaw',
    name: 'Chanel Belshaw',
    role: 'Events Planner',
    phone: '01623 622098',
    email: 'clipstonesocialclub@yahoo.com',
  },
  {
    id: 'dawn-pottse',
    name: 'Dawn Pottse',
    role: 'Events Co-ordinator',
    phone: '01623 622098',
    email: 'clipstonesocialclub@yahoo.com',
  },
];

const AdminSocialStaff = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);
  const [staff, setStaff] = useState([]);
  const [staffForm, setStaffForm] = useState({ name: '', role: '', phone: '', email: '' });
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const storedStaff = window.localStorage.getItem(SOCIAL_STAFF_STORE_KEY);
    if (storedStaff) {
      try {
        setStaff(JSON.parse(storedStaff));
      } catch (parseErr) {
        console.error('Unable to parse social staff from localStorage:', parseErr);
        setStaff(defaultStaff);
        window.localStorage.setItem(SOCIAL_STAFF_STORE_KEY, JSON.stringify(defaultStaff));
      }
    } else {
      setStaff(defaultStaff);
      window.localStorage.setItem(SOCIAL_STAFF_STORE_KEY, JSON.stringify(defaultStaff));
    }

    const checkAdmin = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          navigate('/login');
          return;
        }

        const { data: isAdmin, error: rpcError } = await supabase.rpc('check_is_admin', {
          p_email: session.user.email,
        });

        if (rpcError || !isAdmin) {
          navigate('/account');
          return;
        }

        await resolveAdminRole(session.user.email);

        setAdminInfo({
          email: session.user.email,
          lastSignIn: session.user.last_sign_in_at,
        });
      } catch (err) {
        console.error('Admin social staff access check error:', err);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    checkAdmin();
  }, [navigate]);

  const persistStaff = (nextStaff) => {
    setStaff(nextStaff);
    window.localStorage.setItem(SOCIAL_STAFF_STORE_KEY, JSON.stringify(nextStaff));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setDeleteConfirmId(null);

    if (!staffForm.name.trim()) {
      setError('Staff name is required.');
      return;
    }

    if (editingStaffId) {
      const updated = staff.map((member) => (
        member.id === editingStaffId
          ? {
              id: editingStaffId,
              name: staffForm.name.trim(),
              role: staffForm.role.trim(),
              phone: staffForm.phone.trim(),
              email: staffForm.email.trim(),
            }
          : member
      ));
      persistStaff(updated);
      appendAuditLogEntry({
        actorEmail: adminInfo?.email,
        action: 'Updated',
        entityType: 'social staff',
        entityName: staffForm.name.trim(),
        details: 'Updated social staff profile details.',
      });
      setMessage('Social staff member updated.');
    } else {
      const newMember = {
        id: `staff-${Date.now()}`,
        name: staffForm.name.trim(),
        role: staffForm.role.trim(),
        phone: staffForm.phone.trim(),
        email: staffForm.email.trim(),
      };
      const updated = [newMember, ...staff];
      persistStaff(updated);
      appendAuditLogEntry({
        actorEmail: adminInfo?.email,
        action: 'Added',
        entityType: 'social staff',
        entityName: newMember.name,
        details: 'Added social staff profile to public social page.',
      });
      setMessage('Social staff member added.');
    }

    setStaffForm({ name: '', role: '', phone: '', email: '' });
    setEditingStaffId(null);
  };

  const handleEdit = (member) => {
    setError('');
    setMessage('');
    setDeleteConfirmId(null);
    setEditingStaffId(member.id);
    setStaffForm({
      name: member.name || '',
      role: member.role || '',
      phone: member.phone || '',
      email: member.email || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (member) => {
    const updated = staff.filter((item) => item.id !== member.id);
    persistStaff(updated);
    appendAuditLogEntry({
      actorEmail: adminInfo?.email,
      action: 'Deleted',
      entityType: 'social staff',
      entityName: member.name,
      details: 'Removed social staff profile from public social page.',
    });
    setMessage(`${member.name} was removed.`);
    setError('');
    setDeleteConfirmId(null);
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}><p style={styles.mutedText}>Loading social staff management...</p></div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <img src={logo} alt="Clipstone Miners Welfare Logo" style={styles.logo} />
        </div>

        <div style={styles.header}>
          <div style={styles.adminBadge}>Admin</div>
          <h2 style={styles.title}>Social Staff Management</h2>
          <p style={styles.subtitle}>Add, edit, or remove social staff cards</p>
        </div>

        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Signed in as</span>
          <span style={styles.infoValue}>{adminInfo?.email}</span>
        </div>

        <div style={styles.sectionCard}>
          <h3 style={styles.sectionHeading}>{editingStaffId ? 'Edit social staff member' : 'Add social staff member'}</h3>
          {error && <div style={styles.errorBanner}>{error}</div>}
          {message && <div style={styles.successBanner}>{message}</div>}
          <form onSubmit={handleSubmit} style={styles.formCard}>
            <input
              style={styles.input}
              value={staffForm.name}
              onChange={(event) => setStaffForm({ ...staffForm, name: event.target.value })}
              placeholder="Full name"
            />
            <input
              style={styles.input}
              value={staffForm.role}
              onChange={(event) => setStaffForm({ ...staffForm, role: event.target.value })}
              placeholder="Role"
            />
            <input
              style={styles.input}
              value={staffForm.phone}
              onChange={(event) => setStaffForm({ ...staffForm, phone: event.target.value })}
              placeholder="Phone"
            />
            <input
              style={styles.input}
              value={staffForm.email}
              onChange={(event) => setStaffForm({ ...staffForm, email: event.target.value })}
              placeholder="Email"
            />
            <div style={styles.formBtnRow}>
              <button type="submit" style={styles.primaryBtn}>{editingStaffId ? 'Update member' : 'Save member'}</button>
              {editingStaffId && <button type="button" onClick={() => { setEditingStaffId(null); setStaffForm({ name: '', role: '', phone: '', email: '' }); }} style={styles.secondaryBtn}>Cancel</button>}
            </div>
          </form>
        </div>

        <div style={styles.sectionCard}>
          <h3 style={styles.sectionHeading}>Current social staff</h3>
          {staff.length === 0 ? (
            <p style={styles.mutedText}>No social staff entries yet.</p>
          ) : (
            staff.map((member) => (
              <div key={member.id} style={styles.listItem}>
                <div>
                  <div style={styles.listItemTitle}>{member.name}</div>
                  <div style={styles.listItemMeta}>{member.role || 'No role set'}</div>
                  <div style={styles.listItemMeta}>{member.phone || 'No phone'} {member.email ? `• ${member.email}` : ''}</div>
                </div>
                <div style={styles.listItemActions}>
                  <button type="button" style={styles.actionButton} onClick={() => handleEdit(member)}>Edit</button>
                  {deleteConfirmId === member.id ? (
                    <div style={styles.deleteConfirmWrap}>
                      <span style={styles.deleteConfirmText}>Delete?</span>
                      <button type="button" style={styles.confirmDeleteButton} onClick={() => handleDelete(member)}>Yes</button>
                      <button type="button" style={styles.cancelDeleteButton} onClick={() => setDeleteConfirmId(null)}>No</button>
                    </div>
                  ) : (
                    <button type="button" style={styles.deleteButton} onClick={() => setDeleteConfirmId(member.id)}>Delete</button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div style={styles.actions}>
          <button style={styles.secondaryBtn} onClick={() => navigate('/admin-account')}>
            <i className="icon-left" style={styles.actionIcon} />
            Back to admin dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(16px, 4vw, 40px) clamp(12px, 3vw, 20px)',
    background: 'linear-gradient(135deg, #0f1f18 0%, #1e3f32 100%)',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  card: {
    background: '#1a2e25',
    border: '1px solid #2d4f3c',
    borderRadius: '16px',
    padding: 'clamp(18px, 4vw, 40px)',
    width: '100%',
    maxWidth: '960px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  logo: {
    height: 'clamp(60px, 12vw, 80px)',
    width: 'auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  adminBadge: {
    display: 'inline-block',
    background: '#4ade80',
    color: '#0f1f18',
    fontSize: '11px',
    fontWeight: '800',
    letterSpacing: '0.1em',
    padding: '4px 12px',
    borderRadius: '20px',
    marginBottom: '10px',
  },
  title: {
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 0 4px 0',
  },
  subtitle: {
    color: '#6b9e82',
    fontSize: '14px',
    margin: 0,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#152318',
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '20px',
    gap: '8px',
  },
  infoLabel: {
    color: '#6b9e82',
    fontSize: '13px',
    fontWeight: '500',
  },
  infoValue: {
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '600',
    textAlign: 'right',
    wordBreak: 'break-word',
  },
  sectionCard: {
    background: '#152318',
    border: '1px solid #2d4f3c',
    borderRadius: '12px',
    padding: '18px',
    marginBottom: '20px',
  },
  sectionHeading: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '700',
    margin: '0 0 10px 0',
  },
  mutedText: {
    color: '#6b9e82',
    fontSize: '14px',
    margin: 0,
  },
  formCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  input: {
    width: '100%',
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    padding: '10px 12px',
    background: '#1a2e25',
    color: '#ffffff',
    fontSize: '13px',
    boxSizing: 'border-box',
  },
  formBtnRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    padding: '10px 0',
    borderBottom: '1px solid #22392c',
  },
  listItemTitle: {
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '700',
  },
  listItemMeta: {
    color: '#6b9e82',
    fontSize: '12px',
    marginTop: '2px',
  },
  listItemActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  actionButton: {
    padding: '8px 12px',
    background: '#2d4f3c',
    color: '#ffffff',
    border: '1px solid #4ade80',
    borderRadius: '8px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  deleteButton: {
    padding: '8px 12px',
    background: '#991b1b',
    color: '#ffffff',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  deleteConfirmWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#3a1a1a',
    border: '1px solid #7f1d1d',
    borderRadius: '8px',
    padding: '6px 8px',
  },
  deleteConfirmText: {
    color: '#fecaca',
    fontSize: '12px',
    fontWeight: '600',
  },
  confirmDeleteButton: {
    border: 'none',
    background: '#dc2626',
    color: '#fff',
    borderRadius: '6px',
    padding: '5px 8px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  cancelDeleteButton: {
    border: '1px solid #475569',
    background: 'transparent',
    color: '#cbd5e1',
    borderRadius: '6px',
    padding: '5px 8px',
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  errorBanner: {
    background: '#3a1a1a',
    color: '#f87171',
    border: '1px solid #7f1d1d',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '12px',
    marginBottom: '8px',
  },
  successBanner: {
    background: '#0f2a1a',
    color: '#4ade80',
    border: '1px solid #14532d',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '12px',
    marginBottom: '8px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
  },
  primaryBtn: {
    padding: '12px 14px',
    background: '#4ade80',
    color: '#0f1f18',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '12px 14px',
    background: 'transparent',
    color: '#6b9e82',
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  actionIcon: {
    display: 'inline-block',
    fontSize: '1.05em',
    marginRight: '8px',
    verticalAlign: 'middle',
  },
};

export default AdminSocialStaff;
